import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { Bookmark, Boxes, Handshake, History, Search, UserRound, X, type LucideIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, Skeleton, cn } from "@tsi/ui";
import { formatCurrency, maskPhone } from "@tsi/domain";
import { fetcher } from "@/shared/api/http";

// Busca do topo (⌘K): cartas, reservas, parceiros e usuários, direto na API (GET /busca).
// Vendas, compras e clientes ainda são de exemplo no sistema, então ficam de fora.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface RespostaBusca {
  cartas: { id: string; referencia: string; segmento: "imovel" | "automovel"; credito: number; entrada: number; status: string; desativada: boolean }[];
  reservas: { id: string; numero: number; cliente: string; status: "aguardando" | "confirmada" | "recusada" | "parcial"; origem: "app" | "victoria" | "interna"; quem: string | null; criadaEm: string }[];
  parceiros: { id: string; nome: string; email: string; telefone: string | null; status: string }[];
  usuarios: { id: string; nome: string; email: string; ativo: boolean }[];
}

type Grupo = "reserva" | "carta" | "parceiro" | "usuario";

interface Resultado {
  id: string;
  grupo: Grupo;
  titulo: string;
  detalhe: string;
  href: string;
}

const GRUPO: Record<Grupo, { label: string; icon: LucideIcon; tom: string }> = {
  reserva: { label: "Reservas", icon: Bookmark, tom: "bg-brand-green-50 text-brand-green-700" },
  carta: { label: "Estoque", icon: Boxes, tom: "bg-brand/10 text-brand" },
  parceiro: { label: "Parceiros", icon: Handshake, tom: "bg-amber-50 text-amber-700" },
  usuario: { label: "Usuários", icon: UserRound, tom: "bg-secondary text-foreground" },
};
const ORDEM: Grupo[] = ["reserva", "carta", "parceiro", "usuario"];

const STATUS_RESERVA = { aguardando: "aguardando", confirmada: "confirmada", recusada: "recusada", parcial: "aprovada em parte" } as const;
const ORIGEM = { app: "App", victoria: "VictorIA PRO", interna: "Interna" } as const;
const STATUS_CARTA: Record<string, string> = { disponivel: "disponível", reservada: "reservada", indisponivel: "fora da planilha" };
const STATUS_PARCEIRO: Record<string, string> = { ativo: "ativo", pendente: "aguardando aprovação", bloqueado: "bloqueado" };

const numeroReserva = (n: number) => `R-${String(n).padStart(4, "0")}`;

function resultados(r: RespostaBusca | undefined): Resultado[] {
  if (!r) return [];
  return [
    ...r.reservas.map((x) => ({
      id: `reserva-${x.id}`,
      grupo: "reserva" as const,
      titulo: `${numeroReserva(x.numero)} · ${x.cliente}`,
      detalhe: [ORIGEM[x.origem], x.quem, STATUS_RESERVA[x.status]].filter(Boolean).join(" · "),
      href: `/reservas?status=${x.status === "parcial" ? "confirmada" : x.status}&reserva=${x.id}`,
    })),
    ...r.cartas.map((x) => ({
      id: `carta-${x.id}`,
      grupo: "carta" as const,
      titulo: `Ref ${x.referencia} · ${x.segmento === "imovel" ? "Imóvel" : "Automóvel"}`,
      detalhe: `${formatCurrency(x.credito)} de crédito · entrada ${formatCurrency(x.entrada)} · ${x.desativada ? "desativada" : (STATUS_CARTA[x.status] ?? x.status)}`,
      href: `/estoque?carta=${x.id}`,
    })),
    ...r.parceiros.map((x) => ({
      id: `parceiro-${x.id}`,
      grupo: "parceiro" as const,
      titulo: x.nome,
      detalhe: [x.email, x.telefone && maskPhone(x.telefone), STATUS_PARCEIRO[x.status] ?? x.status].filter(Boolean).join(" · "),
      href: `/parceiros?parceiro=${x.id}`,
    })),
    ...r.usuarios.map((x) => ({
      id: `usuario-${x.id}`,
      grupo: "usuario" as const,
      titulo: x.nome,
      detalhe: `${x.email}${x.ativo ? "" : " · inativo"}`,
      href: "/usuarios",
    })),
  ];
}

const RECENTES_KEY = "tsi:busca-recentes";

function lerRecentes(): Resultado[] {
  try {
    return JSON.parse(localStorage.getItem(RECENTES_KEY) ?? "[]") as Resultado[];
  } catch {
    return [];
  }
}

function guardarRecente(r: Resultado) {
  try {
    const lista = [r, ...lerRecentes().filter((x) => x.id !== r.id)].slice(0, 5);
    localStorage.setItem(RECENTES_KEY, JSON.stringify(lista));
  } catch {
    // sem storage: só não guarda os recentes
  }
}

function useDebounced(valor: string, ms = 250) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function SearchModal({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [ativo, setAtivo] = useState(0);
  const [recentes, setRecentes] = useState<Resultado[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const termo = useDebounced(query.trim());

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setRecentes(lerRecentes());
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const { data, error, isLoading } = useSWR<RespostaBusca>(open && termo ? `/busca?q=${encodeURIComponent(termo)}` : null, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: false,
  });
  // Enquanto o termo digitado não chegou na API, a lista anterior fica (sem piscar).
  const carregando = (!!query.trim() && query.trim() !== termo) || isLoading;
  const lista = useMemo(() => (query.trim() ? resultados(data) : recentes), [query, data, recentes]);

  useEffect(() => setAtivo(0), [termo]);
  useEffect(() => {
    listaRef.current?.querySelector(`[data-indice="${ativo}"]`)?.scrollIntoView({ block: "nearest" });
  }, [ativo]);

  function abrir(r: Resultado) {
    guardarRecente(r);
    onOpenChange(false);
    navigate(r.href);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!lista.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAtivo((i) => (i + 1) % lista.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAtivo((i) => (i - 1 + lista.length) % lista.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = lista[ativo];
      if (r) abrir(r);
    }
  }

  const semTermo = !query.trim();
  const grupos = ORDEM.map((g) => ({ g, itens: lista.filter((r) => r.grupo === g) })).filter((x) => x.itens.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 overflow-hidden p-0" onKeyDown={onKeyDown}>
        <DialogTitle className="sr-only">Buscar</DialogTitle>

        <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3 pr-12">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ref, cliente, CPF/CNPJ, telefone, R-0001, parceiro…"
            aria-label="Buscar"
            className="flex-1 bg-transparent text-[14px] outline-hidden placeholder:text-muted-foreground"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Limpar"
              className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div ref={listaRef} className="max-h-[60vh] overflow-y-auto">
          {semTermo && !recentes.length ? (
            <Dicas />
          ) : !semTermo && carregando && !data ? (
            <CarregandoLista />
          ) : !semTermo && error ? (
            <Aviso titulo="Não foi possível buscar agora." texto="Tente de novo em instantes." />
          ) : !semTermo && !carregando && !lista.length ? (
            <Aviso
              titulo={
                <>
                  Nada encontrado para <span className="font-medium text-foreground">"{query.trim()}"</span>
                </>
              }
              texto="Tente a Ref da carta, o nome do cliente, CPF/CNPJ, telefone ou o número da reserva."
            />
          ) : (
            <div className={cn("py-1 transition-opacity", carregando && "opacity-60")}>
              {semTermo && (
                <div className="flex items-center gap-1.5 px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <History className="h-3 w-3" /> Vistos por último
                </div>
              )}
              {grupos.map(({ g, itens }) => {
                const meta = GRUPO[g];
                return (
                  <div key={g}>
                    {!semTermo && <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{meta.label}</div>}
                    <ul>
                      {itens.map((r) => {
                        const indice = lista.indexOf(r);
                        return (
                          <li key={r.id}>
                            <button
                              type="button"
                              data-indice={indice}
                              onClick={() => abrir(r)}
                              onMouseMove={() => setAtivo(indice)}
                              className={cn("flex w-full items-center gap-3 px-4 py-2 text-left transition-colors", indice === ativo && "bg-secondary/70")}
                            >
                              <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md", meta.tom)}>
                                <meta.icon className="h-4 w-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-[13px] font-medium text-foreground">{r.titulo}</div>
                                <div className="truncate text-[11px] text-muted-foreground">{r.detalhe}</div>
                              </div>
                              <span className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground/70">{meta.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-secondary/30 px-4 py-2 text-[10.5px] text-muted-foreground">
          <span>
            Buscando em <span className="font-medium text-foreground">reservas, estoque, parceiros e usuários</span>
          </span>
          <span className="hidden items-center gap-3 sm:flex">
            <Tecla tecla="↑↓" acao="navegar" />
            <Tecla tecla="↵" acao="abrir" />
            <Tecla tecla="esc" acao="fechar" />
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Tecla({ tecla, acao }: { tecla: string; acao: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <kbd className="rounded border border-border bg-card px-1 font-mono">{tecla}</kbd>
      {acao}
    </span>
  );
}

function CarregandoLista() {
  return (
    <div className="space-y-1 py-2" aria-busy>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-2">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-2/5" />
            <Skeleton className="h-2.5 w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Aviso({ titulo, texto }: { titulo: React.ReactNode; texto: string }) {
  return (
    <div className="px-4 py-12 text-center">
      <div className="text-[13px] text-muted-foreground">{titulo}</div>
      <p className="mt-1 text-[11.5px] text-muted-foreground/70">{texto}</p>
    </div>
  );
}

function Dicas() {
  const dicas = [
    "A Ref de uma carta, como 8806",
    "O número da reserva, como R-0007",
    "Nome, CPF/CNPJ, telefone ou e-mail do cliente",
    "Nome, e-mail ou celular de um parceiro",
    '"imóvel" ou "automóvel" para ver cartas do segmento',
  ];
  return (
    <div className="px-4 py-6">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Você pode buscar por</div>
      <ul className="space-y-1">
        {dicas.map((t) => (
          <li key={t} className="text-[12px] text-muted-foreground">
            · {t}
          </li>
        ))}
      </ul>
    </div>
  );
}
